import moment from "moment";

import {
  JobFrequencyEnum,
  TJobFormData,
  isDayPatternInUse,
} from "./CreateJobAndSchedule.model";
import { DayPatternEntry, describeInvalidPatternDays } from "./day-pattern";

/**
 * One clash `POST /job/check-conflicts` found. The endpoint takes the very body
 * `POST /job` takes and expands the occurrences itself from the same
 * planCreateShifts the save uses, so nothing here is computed on the client: a
 * client-side expansion could disagree with the save's, and the preflight would
 * then report a clash no save refuses, or miss one it does.
 *
 * `start`/`end` are the OTHER shift's window, never a restatement of the hours
 * the user just typed - for `source: "proposed"` that is the other day of this
 * same proposal. They are ISO instants, unlike the day-pattern save's
 * already-localised "YYYY-MM-DD"/"HH:mm" strings, so they do have to be
 * formatted (see describeConflictWindow).
 */
export type ScheduleConflict = {
  employeeId: string;
  /** Falls back to the id when the backend cannot load the name. */
  employeeName: string;
  jobName: string;
  start: string;
  end: string;
  kind: "overlap" | "touching";
  source: "existing" | "proposed";
};

/** What `POST /job/check-conflicts` answers with. */
export type ConflictCheckResult = {
  /**
   * Optional here only because an older API - and the `{}` a stubbed request
   * resolves to - carries no such field, and an absent one has to read as
   * "nothing to report" rather than crash the form it belongs to.
   */
  conflicts?: ScheduleConflict[] | null;
};

/** A conflict said in the scheduler's words, with the style it should carry. */
export type ConflictMessage = {
  /** Stable within one list, so React can key the lines. */
  key: string;
  severity: "error" | "warn";
  text: string;
};

/**
 * Whether the save must be refused.
 *
 * `overlap` only. A `touching` conflict is a warning and never blocks: two
 * back-to-back shifts are a scheduler's deliberate choice, the backend's own
 * assertNoConflicts keeps only `overlap`, and the save would therefore succeed -
 * blocking here would refuse something the server permits, with no way for the
 * user to get past it.
 */
export const hasBlockingConflict = (
  conflicts?: ScheduleConflict[] | null,
): boolean =>
  Boolean(conflicts?.some((conflict) => conflict.kind === "overlap"));

/**
 * The other shift's window, in the browser's zone, through the form's own date
 * library (moment - the project has no luxon and no moment-timezone).
 *
 * The end drops its date when it falls on the start's calendar day, which is the
 * common case and reads far better; an overnight shift keeps both, because
 * "5:00 PM - 1:00 AM" with no second date is the one case where the reader
 * cannot tell which day the end is on.
 *
 * Caveat worth knowing: this renders in the BROWSER's zone, not necessarily the
 * job's. On this form they are the same thing - there is no Time Zone control
 * any more and resolveTimeZone falls back to the browser's zone - so the hours
 * shown are the hours the scheduler typed. A job whose stored zone differs
 * would need a zone-aware formatter here.
 */
export const describeConflictWindow = (start: string, end: string): string => {
  const from = moment(start);
  const to = moment(end);

  if (!from.isValid() || !to.isValid()) {
    // Never invent a window: an unparseable instant must not turn into
    // "Invalid date" in a sentence the scheduler is meant to act on.
    return "";
  }

  const sameDay = from.isSame(to, "day");

  return `${from.format("ddd MMM D, h:mm A")} - ${
    sameDay ? to.format("h:mm A") : to.format("ddd MMM D, h:mm A")
  }`;
};

const describeConflict = (conflict: ScheduleConflict): string => {
  const window = describeConflictWindow(conflict.start, conflict.end);
  // Degrades to the bare sentence rather than a dangling comma when the window
  // cannot be formatted.
  const when = window ? ` ${window}` : "";

  if (conflict.source === "proposed") {
    // Deliberately never names a booking. Told "Ana is already on this job"
    // about two days they have only just entered, a scheduler goes looking for
    // a booking that does not exist - which is the whole reason `source` is a
    // field on the response rather than something the client infers.
    return conflict.kind === "overlap"
      ? `${conflict.employeeName} is on two days of this job at once - the other day runs${when}. Nothing is booked yet; both days are ones you have just entered. Change one day's hours or crew before saving.`
      : `${conflict.employeeName} works two days of this job back to back - the other day runs${when}, with no gap. Both days are ones you have just entered. You can still save.`;
  }

  return conflict.kind === "overlap"
    ? `${conflict.employeeName} is already scheduled on ${conflict.jobName},${when}, which overlaps these hours. Change the hours or the crew before saving.`
    : `${conflict.employeeName} is already scheduled on ${conflict.jobName},${when}, which runs right up against these hours with no gap. You can still save.`;
};

/**
 * Every conflict as its own line, which is how this form already treats
 * day-level problems: the schema's one form-level message exists only to fail
 * the submit and is never rendered, because a summary away from the thing at
 * fault tells the user nothing they can act on.
 *
 * Identical sentences are collapsed. A recurring proposal is checked occurrence
 * by occurrence, so the same person against the same other job can come back
 * several times; where the window differs the lines are genuinely different news
 * and all of them are kept.
 *
 * Errors before warnings, so a blocking clash is never pushed below the
 * back-to-back note it shares the list with.
 */
export const describeScheduleConflicts = (
  conflicts?: ScheduleConflict[] | null,
): ConflictMessage[] => {
  const seen = new Set<string>();
  const messages: ConflictMessage[] = [];

  (conflicts ?? []).forEach((conflict) => {
    const text = describeConflict(conflict);

    if (seen.has(text)) {
      return;
    }
    seen.add(text);

    messages.push({
      key: `${conflict.employeeId}|${conflict.kind}|${conflict.source}|${conflict.start}|${conflict.end}|${conflict.jobName}`,
      severity: conflict.kind === "overlap" ? "error" : "warn",
      text,
    });
  });

  return [
    ...messages.filter((message) => message.severity === "error"),
    ...messages.filter((message) => message.severity === "warn"),
  ];
};

/**
 * Every occurrence of one recurring clash, folded into a single line. `weekday`
 * is the day card (0 = Sunday, as moment's `day()` and DayPatternEntry have it)
 * the line belongs under.
 */
export type GroupedConflict = {
  key: string;
  weekday: number;
  severity: "error" | "warn";
  text: string;
  count: number;
};

/**
 * Conflicts folded by who, against what, and at which hours on which weekday.
 *
 * A recurring clash comes back once per occurrence - the same person against
 * the same other job every Friday, sixteen times - and each occurrence's window
 * carries its own date, so describeScheduleConflicts cannot collapse them.
 * Here the key drops the date and keeps the local weekday and clock times, so
 * those sixteen are one line that says how many dates and over what span.
 *
 * Errors before warnings, then weekday, then employee name, so the order is
 * stable between renders. Unparseable instants are skipped: a line built from
 * "Invalid date" would tell the scheduler nothing they could act on.
 */
export const groupScheduleConflicts = (
  conflicts?: ScheduleConflict[] | null,
): GroupedConflict[] => {
  type Bucket = {
    conflict: ScheduleConflict;
    weekday: number;
    window: string;
    firstStart: moment.Moment;
    lastStart: moment.Moment;
    count: number;
  };

  const buckets = new Map<string, Bucket>();

  (conflicts ?? []).forEach((conflict) => {
    const from = moment(conflict.start);
    const to = moment(conflict.end);

    if (!from.isValid() || !to.isValid()) {
      return;
    }

    const weekday = from.day();
    const window = `${from.format("h:mm A")} - ${to.format("h:mm A")}`;
    const key = [
      conflict.employeeId,
      conflict.jobName,
      conflict.kind,
      conflict.source,
      weekday,
      from.format("h:mm A"),
      to.format("h:mm A"),
    ].join("|");
    const bucket = buckets.get(key);

    if (!bucket) {
      buckets.set(key, {
        conflict,
        weekday,
        window,
        firstStart: from,
        lastStart: from,
        count: 1,
      });
      return;
    }

    bucket.count += 1;
    if (from.isBefore(bucket.firstStart)) bucket.firstStart = from;
    if (from.isAfter(bucket.lastStart)) bucket.lastStart = from;
  });

  const groups = Array.from(buckets.entries()).map(([key, bucket]) => {
    const { conflict, window, count } = bucket;
    const overlap = conflict.kind === "overlap";
    const dates =
      count > 1
        ? ` (${count} dates, ${bucket.firstStart.format("MMM D")} \u2013 ${bucket.lastStart.format("MMM D")})`
        : ` (${bucket.firstStart.format("ddd MMM D")})`;
    const name = conflict.employeeName;

    // A proposed clash is between two days of this same job and must never name
    // another booking - see describeConflict.
    const text =
      conflict.source === "proposed"
        ? overlap
          ? `${name} is on two days of this job at once - the other day runs ${window}${dates}. Nothing is booked yet; both days are ones you have just entered. Change one day's hours or crew before saving.`
          : `${name} works two days of this job back to back - the other day runs ${window}${dates}, with no gap. Both days are ones you have just entered. You can still save.`
        : `${name} is also scheduled on ${conflict.jobName}, ${window}${dates}${
            overlap
              ? ". Change the hours or the crew before saving."
              : ", right up against these hours. You can still save."
          }`;

    return {
      group: {
        key,
        weekday: bucket.weekday,
        severity: (overlap ? "error" : "warn") as "error" | "warn",
        text,
        count,
      },
      name,
    };
  });

  const rank = (severity: "error" | "warn") => (severity === "error" ? 0 : 1);

  return groups
    .sort(
      (a, b) =>
        rank(a.group.severity) - rank(b.group.severity) ||
        a.group.weekday - b.group.weekday ||
        a.name.localeCompare(b.name),
    )
    .map(({ group }) => group);
};

const isUsableTime = (value: unknown): boolean =>
  Boolean(value) && moment(value as any).isValid();

/**
 * Whether the body is complete enough for the endpoint to answer at all.
 *
 * The preflight is the same route as the create as far as validation goes, so a
 * half-filled body does not come back as "no conflicts" - it comes back 400.
 * Not new - `create` validates the same DTO - but the preflight fires while the
 * form is still being typed into, so it meets a half-filled body constantly.
 *
 * Only the fields the CreateJobDto actually requires, plus the ones the
 * expansion dereferences. `employeeIds` is in here for a different reason: with
 * no crew anywhere there is nobody to double-book, so collectConflicts returns
 * an empty list and the request buys nothing.
 */
export const canPreflightConflicts = (form: Partial<TJobFormData>): boolean => {
  if (
    !form.leadId ||
    !form.name ||
    !form.invoiceGeneration ||
    form.zoneCode === null ||
    form.zoneCode === undefined ||
    !isUsableTime(form.startTime) ||
    !isUsableTime(form.endTime)
  ) {
    return false;
  }

  const patternInUse = isDayPatternInUse(form);

  if (patternInUse) {
    // The same problems the cards are already showing. Sent anyway, a blank
    // time or a repeated weekday is a 400 from the DTO, not a conflicts list.
    if (
      describeInvalidPatternDays((form.dayPattern ?? []) as DayPatternEntry[])
        .length > 0
    ) {
      return false;
    }
  } else if (!form.employeeIds?.length) {
    // Without the pattern the job-level crew is the only crew there is.
    return false;
  }

  if (!form.recurrence) {
    return true;
  }

  if (!form.frequency || !isUsableTime(form.recurrenceEndDate)) {
    return false;
  }

  // A weekly body with no weekdays and no pattern plans no occurrence at all:
  // planWeeklyWindows tests `daysOfWeek.includes(...)` per candidate day and an
  // empty list matches none. The answer would be a truthful, useless "no
  // conflicts" about a job that would never be saved - create refuses this very
  // body with emptyRecurrenceMessage - so there is nothing worth asking.
  //
  // (An ABSENT daysOfWeek would instead throw a 500 inside that dereference.
  // This form cannot send one: the field defaults to [] and the payload builder
  // always passes it through. Worth knowing if another caller is ever added.)
  if (
    !patternInUse &&
    form.frequency === JobFrequencyEnum.WEEKLY &&
    !form.daysOfWeek?.length
  ) {
    return false;
  }

  return true;
};
